import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type {
  BoardColumn,
  ClientRequest,
  InternalRequest,
  Paginated,
  Priority,
  WorkType,
} from '@rundown/types';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';

/**
 * Requests transport.
 *
 * ── ASSUMED CONTRACT ──────────────────────────────────────────────────────────
 *   GET  /workspaces/:workspaceId/requests  -> Paginated<ClientRequest|InternalRequest>
 *   GET  /workspaces/:workspaceId/requests/:id
 *   POST /workspaces/:workspaceId/requests  { title, description } -> the created row
 *
 * The workspace id is in the path even though the server could derive it from the
 * session. That is intentional: it makes the tenant explicit in logs and traces,
 * and it means a request for the wrong workspace is a 403 rather than a silent
 * read of the wrong data. The server must still verify the path id against the
 * session — the client asserting it proves nothing.
 */

/**
 * What comes back depends on who is asking: a client user receives
 * `ClientRequest`, a Noorix user with `internal.read` receives `InternalRequest`,
 * which is a superset. The server decides; the client narrows.
 */
export type RequestRow = ClientRequest | InternalRequest;

/** `boardColumn` exists only on the internal projection — see @rundown/types. */
export function isInternalRequest(request: RequestRow): request is InternalRequest {
  return 'boardColumn' in request;
}

function requestsPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/requests`;
}

/**
 * The list endpoint's query string, as an object.
 *
 * `cursor` is the whole point of the type existing. The server has always been
 * keyset-paginated — `.limit(limit + 1)`, a computed `hasMore`, a `nextCursor`
 * — and this transport used to send no parameters and throw the cursor away, so
 * Posts stopped dead at the server's default of 50 rows and nothing anywhere
 * could reach the 51st.
 */
export interface RequestListQuery {
  /** `nextCursor` from the previous page. Null/absent asks for the first one. */
  readonly cursor?: string | null;
  /** Server default 50, ceiling 100 — see ListRequestsSchema. */
  readonly limit?: number;
  readonly needsReview?: boolean;
  /**
   * The cursor is read in whatever the sort orders by, so this decides what a
   * cursor even means: `createdAt` for 'recent', `backlogRank` for 'backlog'.
   * Never change it between pages of one list.
   */
  readonly sort?: 'backlog' | 'recent';
}

export function fetchRequests(
  workspaceId: string,
  query: RequestListQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<RequestRow>> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.sort !== undefined) params.set('sort', query.sort);
  // Spelled out rather than coerced: the server reads this as the literal
  // strings 'true'/'false' and rejects anything else, which is what stops
  // "false" from arriving as a truthy filter.
  if (query.needsReview !== undefined) params.set('needsReview', String(query.needsReview));
  if (query.cursor !== undefined && query.cursor !== null) params.set('cursor', query.cursor);

  const search = params.toString();
  return api.get<Paginated<RequestRow>>(
    search === '' ? requestsPath(workspaceId) : `${requestsPath(workspaceId)}?${search}`,
    signal,
  );
}

export function fetchRequest(
  workspaceId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<RequestRow> {
  return api.get<RequestRow>(`${requestsPath(workspaceId)}/${encodeURIComponent(requestId)}`, signal);
}

export interface NewRequestInput {
  readonly title: string;
  /**
   * The rich-text document, when the screen that posted it had one. The plain
   * `description` below stays the fallback — see CreateRequestSchema, which
   * derives the text from this when it is present.
   */
  readonly descriptionDoc?: unknown;
  /** Files uploaded while the client was still typing; only the ids travel. */
  readonly attachmentIds?: readonly string[];
  /**
   * Plain text for now. `ClientRequest.description` is typed `unknown` because it
   * will become a Tiptap/ProseMirror JSON document — see the TODO in
   * RequestComposer.tsx. The server is expected to accept a string today and
   * store it as a single paragraph.
   */
  readonly description: string;
}

export function createRequest(workspaceId: string, input: NewRequestInput): Promise<RequestRow> {
  return api.post<RequestRow>(requestsPath(workspaceId), input);
}

/**
 * Noorix filing work directly — no ask box, no AI, no review queue.
 *
 * The same endpoint with `manual: true`, which the server refuses for a client
 * role: a client is never asked to pick a type, because a type is a billing
 * classification. See CreateRequestSchema on the API side.
 */
export interface ManualRequestInput {
  readonly title: string;
  readonly description: string;
  /** The same document shape the request description is edited in afterwards. */
  readonly descriptionDoc?: unknown;
  readonly type: WorkType;
  readonly requestedPriority: Priority;
  readonly approxHours: number;
  readonly visibleToClient: boolean;
  /** Which project to file it under, by id. The form offers the existing ones. */
  readonly projectId: string | null;
}

export function createRequestManually(workspaceId: string, input: ManualRequestInput): Promise<RequestRow> {
  return api.post<RequestRow>(requestsPath(workspaceId), { ...input, manual: true });
}

/**
 * A field edit.
 *
 * Every key is optional and a missing key means "leave it alone" — so send only
 * what changed, never the whole row. Sending the lot would make every save a
 * last-write-wins overwrite of fields somebody else may have just edited, and
 * would put an activity entry in the trail for each of them.
 *
 * `null` is not the same as absent: it clears the field. The server holds the
 * same distinction.
 */
export interface RequestPatch {
  readonly title?: string;
  /**
   * A rich-text document — the same ProseMirror shape a message body uses,
   * validated by the same parser server-side. `unknown` rather than a modelled
   * doc type for the same reason `ClientRequest.description` is: the shape is
   * the editor's, and typing it here would be a second, drifting copy of a
   * schema the server already owns.
   *
   * The server derives `descriptionText` from it. Nothing sends that field.
   */
  readonly description?: unknown;
  readonly type?: WorkType;
  readonly priority?: Priority;
  /** Hours on the wire; the server stores minutes. */
  readonly approxHours?: number;
  readonly projectId?: string | null;
  /**
   * Which sprint this work is committed to; null pulls it back out.
   *
   * Gated on `planning.write` server-side, not `request.review` — the client
   * ranks the backlog, Noorix answers with a week. The server refuses moving
   * work into or out of a locked or released sprint: that takes an agreed
   * change of plan, raised from the Sprints screen.
   */
  readonly sprintId?: string | null;
  /** Who on the Noorix side is building it; null unassigns. `planning.write`. */
  readonly ownerId?: string | null;
  /** The hotfix track. On pulls it out of any sprint; committing to a sprint turns it off. */
  readonly isHotfix?: boolean;
  readonly boardColumn?: BoardColumn;
  readonly visibleToClient?: boolean;
}

export function updateRequest(
  workspaceId: string,
  requestId: string,
  patch: RequestPatch,
): Promise<RequestRow> {
  return api.patch<RequestRow>(`${requestsPath(workspaceId)}/${encodeURIComponent(requestId)}`, patch);
}

/** The Review inbox's two outcomes. Both take the request out of the inbox. */
/**
 * One decision, not two. `ask_client` was removed: Noorix asks a client by
 * writing in the thread, which is open on the same screen, so a button whose
 * whole function was to focus that box was furniture — and it cleared
 * `needs_review` on the way, filing requests nobody had asked anything about.
 * See ReviewRequestSchema on the API side.
 */
export type ReviewDecision = 'confirmed';

export function reviewRequest(
  workspaceId: string,
  requestId: string,
  decision: ReviewDecision,
): Promise<RequestRow> {
  return api.post<RequestRow>(`${requestsPath(workspaceId)}/${encodeURIComponent(requestId)}/review`, {
    decision,
  });
}

/**
 * A page of Posts. The server's own default; kept explicit because the page
 * size and the cursor have to stay in step across a list's pages.
 */
const REQUESTS_PAGE_SIZE = 50;

/** The Review inbox reads in bigger bites — it is a queue somebody works through. */
const REVIEW_QUEUE_PAGE_SIZE = 100;

/**
 * Query options live next to the fetchers so the key and the function that fills
 * it can never drift apart. Every key starts ['ws', workspaceId] — see queryKeys.
 *
 * Infinite rather than plain: `nextCursor` is the server telling us there are
 * more rows, and a plain query has nowhere to put that answer. `requests` is
 * the sort=backlog list, so the cursor here is a `backlogRank`.
 *
 * The key is unchanged, so every existing `invalidateQueries` — the realtime
 * feed's, and the request/intake mutations' — still matches it. Invalidating an
 * infinite query refetches the pages already loaded rather than collapsing back
 * to the first, so a socket event cannot silently throw away what someone has
 * already paged in.
 */
export function requestsInfiniteQueryOptions(workspaceId: string) {
  return infiniteQueryOptions({
    queryKey: queryKeys.requests(workspaceId),
    queryFn: ({ pageParam, signal }) =>
      fetchRequests(workspaceId, { limit: REQUESTS_PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    // Null is how the server says "that was the last page", and it is also how
    // TanStack Query reads "no next page" — so this passes straight through and
    // `hasNextPage` becomes false on its own.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function requestQueryOptions(workspaceId: string, requestId: string) {
  return queryOptions({
    queryKey: queryKeys.request(workspaceId, requestId),
    queryFn: ({ signal }) => fetchRequest(workspaceId, requestId, signal),
  });
}

/**
 * What still needs a decision from Noorix.
 *
 * Sorted by arrival rather than by backlog rank: this is a queue of things that
 * turned up, and the oldest unreviewed request is the one that has been waiting
 * on Noorix longest — which rank, being the client's ordering, says nothing
 * about.
 *
 * It has its own cache key rather than filtering the Posts list in the browser,
 * because the two lists answer different questions and one of them is bounded by
 * `needs_review` on the server.
 *
 * `limit=100` used to be hard-coded here with no cursor — and 100 is the
 * server's ceiling, not a coincidence, so an inbox that reached it had no way
 * left to go further: the 101st unreviewed request was unreachable from this
 * screen by any means. Paging is the fix; raising the limit is not, because
 * there is no limit the server will accept that a backlog cannot exceed.
 *
 * sort=recent, so the cursor here is a `createdAt` — and it must not be paged
 * against the backlog sort, which orders by rank instead.
 */
export function reviewQueueInfiniteQueryOptions(workspaceId: string) {
  return infiniteQueryOptions({
    queryKey: queryKeys.reviewQueue(workspaceId),
    queryFn: ({ pageParam, signal }) =>
      fetchRequests(
        workspaceId,
        {
          needsReview: true,
          sort: 'recent',
          limit: REVIEW_QUEUE_PAGE_SIZE,
          cursor: pageParam,
        },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

// ---------------------------------------------------------------------------
// Merge and delete — see requests.service.ts
// ---------------------------------------------------------------------------

/**
 * Merge this request into the one it duplicates. The duplicate is linked and
 * closed, not destroyed — its number keeps resolving and its conversation stays
 * where its author left it. See RequestsService.mergeAsDuplicate.
 */
export function mergeAsDuplicate(
  workspaceId: string,
  requestId: string,
  intoRequestId: string,
): Promise<RequestRow> {
  return api.post<RequestRow>(`${requestsPath(workspaceId)}/${encodeURIComponent(requestId)}/merge`, {
    intoRequestId,
  });
}

/**
 * Delete a request for good.
 *
 * `confirm` is the request's own number and the server checks it, so this is
 * not a dialog-only guard — see DeleteRequestSchema. The server also refuses
 * outright for anything with logged time or a frozen sprint scope behind it.
 */
export function deleteRequest(workspaceId: string, requestId: string, confirm: number): Promise<void> {
  return api.delete<void>(`${requestsPath(workspaceId)}/${encodeURIComponent(requestId)}?confirm=${confirm}`);
}
